import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TLabelAutoOrganizationChannel } from '../types/label-auto.type';

/**
 * Get Organization Channel Response Type
 */
export type TGetListLabelAutoOrganizationChannelResponse = ApiResponse<TLabelAutoOrganizationChannel[]>;

/**
 * Get Organization Channel Params Type
 */
export interface TGetListLabelAutoOrganizationChannelParams {
  search?: string;
}

export const getListLabelAutoOrganizationChannel = async (
  params?: TGetListLabelAutoOrganizationChannelParams
): Promise<TGetListLabelAutoOrganizationChannelResponse> => {
  const url = '/auto-label/list-organization-channel';

  return axiosInstance.get(url, { params });
};

/**
 * Get Organization Channel Query Key
 */
export const GET_LIST_LABEL_AUTO_ORGANIZATION_CHANNEL_QUERY_KEY = 'list-label-auto-organization-channel';

export const getListLabelAutoOrganizationChannelQueryOptions = <TData = TLabelAutoOrganizationChannel[]>(
  params?: TGetListLabelAutoOrganizationChannelParams,
  select?: (data: TGetListLabelAutoOrganizationChannelResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_AUTO_ORGANIZATION_CHANNEL_QUERY_KEY,
      {
        search: params?.search,
      },
    ],
    queryFn: () => getListLabelAutoOrganizationChannel(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLabelAutoOrganizationChannelQuery<TData>(options: {
  params?: TGetListLabelAutoOrganizationChannelParams;
  queryConfig?: QueryConfig<typeof getListLabelAutoOrganizationChannelQueryOptions<TData>>;
  select: (data: TGetListLabelAutoOrganizationChannelResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLabelAutoOrganizationChannelQuery(options: {
  params?: TGetListLabelAutoOrganizationChannelParams;
  queryConfig?: QueryConfig<
    typeof getListLabelAutoOrganizationChannelQueryOptions<TGetListLabelAutoOrganizationChannelResponse>
  >;
}): ReturnType<typeof useQuery<TGetListLabelAutoOrganizationChannelResponse>>;

// Implementation
export function useGetListLabelAutoOrganizationChannelQuery<TData = TGetListLabelAutoOrganizationChannelResponse>({
  queryConfig,
  params,
  select,
}: {
  params?: TGetListLabelAutoOrganizationChannelParams;
  queryConfig?: QueryConfig<
    typeof getListLabelAutoOrganizationChannelQueryOptions<TData | TGetListLabelAutoOrganizationChannelResponse>
  >;
  select?: (data: TGetListLabelAutoOrganizationChannelResponse) => TData;
}) {
  return useQuery({
    ...getListLabelAutoOrganizationChannelQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetListLabelAutoOrganizationChannelResponse ? TGetListLabelAutoOrganizationChannelResponse : TData
    >
  >;
}
